import { BrandKitFontOption } from './types';

import { getEnv } from '@/constants';
import { BRAND_KIT_PATH } from '@/hooks/useCustomFont/constants';
import loggerService from '@/services/logger.service';
import { flattenCssColor, getAccessibleForeground, parseCssColor } from '@/utils/contrast';
import { loadFont } from '@/utils/fonts';
import { encodeFileName, getDecodedFileNameFromUrl } from '@/utils/url';

export interface ThemeContrastInput {
  primaryBackground: string;
  secondaryBackground: string;
  primaryForeground: string;
  secondaryForeground: string;
}

export type AccessibleThemeTokens = Record<
  | '--theme-primary-color'
  | '--theme-secondary-color'
  | '--theme-primary-text-color'
  | '--theme-secondary-text-color'
  | '--theme-focus-color'
  | '--theme-control-color'
  | '--theme-selected-color'
  | '--theme-disabled-color'
  | '--theme-error-color',
  string
>;

const DEFAULT_PRIMARY_BACKGROUND = '#262333';
const DEFAULT_SECONDARY_BACKGROUND = '#292f3c';
const TEXT_FALLBACKS = ['#000000', '#ffffff'] as const;
const ERROR_FALLBACKS = ['#b91c1c', '#fecaca', '#000000', '#ffffff'] as const;

const resolveBackground = (color: string, fallback: string): string => {
  const parsed = parseCssColor(color);
  if (!parsed) {
    return fallback;
  }

  return parsed.alpha < 1 ? flattenCssColor(color, fallback) : color;
};

export const createAccessibleThemeTokens = (theme: ThemeContrastInput): AccessibleThemeTokens => {
  const primaryBackground = resolveBackground(theme.primaryBackground, DEFAULT_PRIMARY_BACKGROUND);
  const secondaryBackground = resolveBackground(theme.secondaryBackground, DEFAULT_SECONDARY_BACKGROUND);
  const primaryText = getAccessibleForeground(primaryBackground, [theme.primaryForeground, ...TEXT_FALLBACKS], 4.5);
  const secondaryText = getAccessibleForeground(
    secondaryBackground,
    [theme.secondaryForeground, ...TEXT_FALLBACKS],
    4.5
  );

  return {
    '--theme-primary-color': primaryBackground,
    '--theme-secondary-color': secondaryBackground,
    '--theme-primary-text-color': primaryText,
    '--theme-secondary-text-color': secondaryText,
    '--theme-focus-color': getAccessibleForeground(
      primaryBackground,
      [theme.secondaryForeground, primaryText, ...TEXT_FALLBACKS],
      3
    ),
    '--theme-control-color': getAccessibleForeground(
      secondaryText,
      [theme.secondaryForeground, primaryText, ...TEXT_FALLBACKS],
      3
    ),
    '--theme-selected-color': getAccessibleForeground(
      primaryBackground,
      [theme.primaryForeground, secondaryText, ...TEXT_FALLBACKS],
      3
    ),
    '--theme-disabled-color': getAccessibleForeground(
      secondaryText,
      [theme.primaryForeground, primaryText, ...TEXT_FALLBACKS],
      3
    ),
    '--theme-error-color': getAccessibleForeground(primaryBackground, [...ERROR_FALLBACKS, primaryText], 4.5)
  };
};

export function getFormattedBrandKitBackground(background: string[], orgId: string) {
  return background.map(url => {
    const encodedFileName = encodeFileName(url);
    return `url(${getEnv('VUE_APP_CLOUD_FRONT_URL')}${BRAND_KIT_PATH}/${orgId}/${encodedFileName})`;
  });
}

export async function getFormattedBrandKitFonts(fonts: string[], orgId: string): Promise<BrandKitFontOption[]> {
  const fontPromises = fonts.map(async (font: string) => {
    const url = `${getEnv('VUE_APP_CLOUD_FRONT_URL')}${BRAND_KIT_PATH}/${orgId}/${font}`;
    const fontName = getDecodedFileNameFromUrl(url);

    let isLoaded = true;
    try {
      await loadFont(url, fontName);
    } catch (err) {
      loggerService.warn('Error loading font', err);
      isLoaded = false;
    }

    return {
      value: url,
      label: fontName,
      style: { fontFamily: fontName, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
      disabled: !isLoaded,
      tooltip: isLoaded ? '' : '@@translate@@ Failed to load font',
      tooltipSide: 'top',
      tooltipSideOffset: 1,
      isBrandKitFont: true
    };
  });

  return Promise.all(fontPromises);
}
